// Pending creator attribution — captured when a visitor lands on
// a creator's card (/p/<slug>, optionally with ?utm_* from a boosted
// post) and consumed at signup, where it's forwarded as
// attributionSlug/utmSource/utmCampaign alongside any referral code.
// Server-side resolution runs in creators.attributeCreatorSignup; this
// is purely the web-side carry between the landing hit and the signup
// submit, which may be separated by an OAuth bounce, a tab reload, or
// a "come back later" gap — hence localStorage (same rationale as
// lib/referral.ts, which stays the referral-reward path's own stash).

const KEY_PENDING_ATTRIBUTION = 'wydm.pending_creator_attribution';

export interface PendingAttribution {
  slug?: string;
  utmSource?: string;
  utmCampaign?: string;
}

// Slugs are lowercase alnum+hyphen per lib/slug on the backend; UTM
// values are freeform but capped so a hostile link can't stuff
// localStorage. The backend is the authority on validity — this just
// avoids storing obvious junk.
function clean(raw: string | null, max: number): string | undefined {
  if (!raw) return undefined;
  const t = raw.trim();
  if (!t) return undefined;
  return t.slice(0, max);
}

/**
 * Persist attribution captured from a creator's card link. Merges over
 * any existing entry field-wise (a later UTM-tagged visit to the same
 * card keeps the first slug; a visit to a DIFFERENT creator's card
 * overwrites the slug — last-touch attribution). No-ops on junk.
 */
export function storePendingAttribution(input: {
  slug?: string;
  utmSource?: string;
  utmCampaign?: string;
}): void {
  const slug = clean(input.slug ?? null, 64)?.toLowerCase();
  const utmSource = clean(input.utmSource ?? null, 48);
  const utmCampaign = clean(input.utmCampaign ?? null, 48);
  if (!slug && !utmSource && !utmCampaign) return;
  try {
    const existing = takePeek();
    const merged: PendingAttribution = {
      slug: slug ?? existing?.slug,
      utmSource: utmSource ?? existing?.utmSource,
      utmCampaign: utmCampaign ?? existing?.utmCampaign,
    };
    // A new slug resets UTMs that belonged to the previous card.
    if (slug && existing?.slug && slug !== existing.slug) {
      merged.utmSource = utmSource;
      merged.utmCampaign = utmCampaign;
    }
    localStorage.setItem(KEY_PENDING_ATTRIBUTION, JSON.stringify(merged));
  } catch {
    // Private-mode / quota — attribution is best-effort, so swallow.
  }
}

/** Read without clearing. */
function takePeek(): PendingAttribution | undefined {
  try {
    const raw = localStorage.getItem(KEY_PENDING_ATTRIBUTION);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as PendingAttribution;
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Read and clear the pending attribution. Returns undefined when none
 * is stored. Single-use by design (mirrors takePendingReferralCode):
 * signup reads it exactly once so a later unrelated signup in the same
 * browser isn't mis-attributed.
 */
export function takePendingAttribution(): PendingAttribution | undefined {
  try {
    const raw = localStorage.getItem(KEY_PENDING_ATTRIBUTION);
    if (!raw) return undefined;
    localStorage.removeItem(KEY_PENDING_ATTRIBUTION);
    const parsed = JSON.parse(raw) as PendingAttribution;
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    return undefined;
  }
}
